import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import type { CloudAccountType } from '@labrav/react-components'

import type { AccountDataSelection } from '../../types/accountSelection'
import type { CloudType, ProductType } from '../../types'

const safeHead = <T>(list: T[] | undefined | null): T | undefined => {
  if (!Array.isArray(list) || list.length === 0) return undefined
  return list[0]
}

const EMPTY_ACCOUNTS: AccountDataSelection[] = []

type UseAccountIdRootState = {
  accountSelection?: {
    selectedAccountIds?: Partial<
      Record<string, Partial<Record<CloudType, string>>>
    >
    availableAccounts?: AccountDataSelection[]
  }
}

export const useAccountId = ({
  product,
  cloud,
}: {
  product: ProductType
  cloud?: CloudType
}): string => {
  const { accountId: accountIdFromParams } = useParams<{ accountId: string }>()
  const selectedAccountIds = useSelector(
    (state: UseAccountIdRootState) => state.accountSelection?.selectedAccountIds
  )
  const availableAccounts = useSelector((state: UseAccountIdRootState) => {
    const accounts = state.accountSelection?.availableAccounts
    return Array.isArray(accounts) ? accounts : EMPTY_ACCOUNTS
  })
  const accountData = useMemo(
    () =>
      availableAccounts.filter(
        account => account?.product?.toLowerCase() === product
      ),
    [availableAccounts, product]
  )

  if (accountIdFromParams) {
    return accountIdFromParams
  }

  const accountIdForCloud = (requestedCloud: CloudType) =>
    accountData.find(account => account?.cloud === requestedCloud)?.id || ''

  if (product.toLowerCase() === 'flyout') {
    const flyoutSelections =
      (
        selectedAccountIds as Record<string, Partial<Record<CloudType, string>>>
      )?.['FLYOUT'] || selectedAccountIds?.['flyout']

    if (cloud) {
      return flyoutSelections?.[cloud] || accountIdForCloud(cloud)
    }

    const selectedCloud = Object.keys(flyoutSelections || {})[0] as
      | CloudType
      | undefined
    if (selectedCloud) {
      return flyoutSelections?.[selectedCloud] || ''
    }
  }

  if (cloud) {
    return accountIdForCloud(cloud)
  }

  return safeHead(accountData)?.id || ''
}

export type CloudAccountTypeWithOriginalId = CloudAccountType & {
  originalId: string
}

export const extractAccountIdFromCombinedId = (combinedId: string): string => {
  const parts = combinedId.split('-')
  if (parts.length >= 3) {
    return parts.slice(0, -2).join('-')
  }
  return combinedId
}

export const getSelectedAccountIdWithCloud = (
  accountId: string,
  product: ProductType,
  cloud?: string
): string => {
  if (!cloud || !accountId) return accountId
  const cloudUpper = cloud.toUpperCase()
  return `${accountId}-${product}-${cloudUpper}`
}

const EMPTY_ACCOUNTS_FOR_CLOUD: AccountDataSelection[] = []

export const useCloudAccounts = (product: ProductType) => {
  const accountsData = useSelector((state: UseAccountIdRootState) => {
    const accounts = state.accountSelection?.availableAccounts
    return Array.isArray(accounts) ? accounts : EMPTY_ACCOUNTS_FOR_CLOUD
  })

  return useMemo(() => {
    const marketplaceAccountsData = accountsData.filter(
      account => account.product?.toLowerCase() === product
    )

    const toProvider = (cloud: unknown): 'AWS' | 'AZURE' | 'GCP' | null => {
      const value = typeof cloud === 'string' ? cloud.toUpperCase() : ''
      if (value === 'AWS' || value === 'AZURE' || value === 'GCP') return value
      return null
    }

    return marketplaceAccountsData
      .map(account => {
        const provider = toProvider(account.cloud)
        if (!provider) return null
        const uniqueId = `${account.id}-${product}-${provider}`
        return {
          id: uniqueId,
          name: account.name,
          provider,
          originalId: account.id,
        }
      })
      .filter(Boolean) as CloudAccountTypeWithOriginalId[]
  }, [accountsData, product])
}
